import React from "react";
import * as Sentry from "@sentry/nextjs";
import { RemiError } from "@remi/error";

import ErrorComponent from "~/components/Error";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.withScope((scope) => {
      scope.setExtras({
        componentStack: errorInfo.componentStack,
      });
      if (error instanceof RemiError && error.metric) {
        switch (error.metric.type) {
          case "counter":
            Sentry.metrics.count(error.metric.name, error.metric.value);
            return;
          case "gauge":
            Sentry.metrics.gauge(error.metric.name, error.metric.value);
            return;
          case "distribution":
            Sentry.metrics.distribution(error.metric.name, error.metric.value);
            return;
        }
      }
      Sentry.captureException(error);
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorComponent
          status={500}
          title="Something went wrong"
          message={this.state.error?.message || "An unexpected error occurred"}
        />
      );
    }

    return this.props.children;
  }
}
